import { Box, Button, Card, CardActions, CardContent, Popover, TableCell, TableRow } from "@mui/material";
import { CellValueUnion } from "Backend";
import { displaySimpleCellValue } from "components/FormField/displayCellValue";
import { FormFieldCellEditor } from "components/FormField/FormFieldCellEditor";
import { Cell } from "generated/graphql";
import { FC, useState } from "react";
import { useAppSelector } from "redux/hooks";
import { selectCaseInfo } from "redux/slices/case";
import { EllipsisSX } from "theme";

interface HitTableInputRowProps {
  cells: Cell[];
  applyBulkUpdate: (cell: Cell, value: CellValueUnion) => void;
  numRowsChecked: number;
}

interface BulkEditCellProps {
  cell: Cell;
  applyBulkUpdate: (cell: Cell, value: CellValueUnion) => void;
  numRowsChecked: number;
}
const BulkEditCell: FC<BulkEditCellProps> = ({ cell, applyBulkUpdate, numRowsChecked }) => {
  const caseInfo = useAppSelector(selectCaseInfo);
  const [value, setValue] = useState<CellValueUnion | undefined>(undefined);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleCellClick = (event) => {
    setAnchorEl(event.target);
  };

  const closePopover = () => {
    setAnchorEl(null);
    setValue(undefined);
  };

  const apply = () => {
    if (value !== undefined) {
      applyBulkUpdate(cell, value);
      closePopover();
    }
  };

  return (
    <TableCell style={{ top: "20px" }}>
      <Box
        onClick={handleCellClick}
        sx={{
          zIndex: 1,
          position: "absolute",
          width: "100%",
          height: "100%",
          cursor: "pointer",
        }}
      />
      <FormFieldCellEditor
        variant="dense"
        cell={cell}
        caseInfo={caseInfo}
        label=""
        tabular={false}
        value={value}
        setValue={setValue}
      />
      <Popover
        onClick={(event) => event.stopPropagation()}
        open={!!anchorEl}
        onClose={closePopover}
        PaperProps={{
          sx: { overflow: "visible" }, // date picker doesn't overflow without this
        }}
        anchorEl={anchorEl}>
        <Card>
          <CardContent>
            <FormFieldCellEditor
              variant="dense"
              cell={cell}
              caseInfo={caseInfo}
              label=""
              tabular={false}
              value={value}
              setValue={setValue}
            />
          </CardContent>
          <CardActions>
            <Button onClick={closePopover} color="primary" sx={{ mr: 2 }}>
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={apply}
              disabled={value === undefined || numRowsChecked === 0}
              sx={{ display: "flex" }}>
              <Box sx={{ flexShrink: 0 }}>Apply</Box>
              <Box sx={{ ml: "6px" }}>&#34;</Box>
              <Box sx={{ ...EllipsisSX, maxWidth: 100 }}>{displaySimpleCellValue(value)}</Box>
              <Box sx={{ mr: "6px" }}>&#34;</Box>
              <Box sx={{ flexShrink: 0 }}>to {numRowsChecked} Pins</Box>
            </Button>
          </CardActions>
        </Card>
      </Popover>
    </TableCell>
  );
};

const HitTableInputRow: FC<HitTableInputRowProps> = ({ cells, numRowsChecked, applyBulkUpdate }) => {
  return (
    <TableRow data-pendo="drug-table-input-row">
      {cells.map((cell) => {
        return (
          <BulkEditCell key={cell.id} cell={cell} numRowsChecked={numRowsChecked} applyBulkUpdate={applyBulkUpdate} />
        );
      })}
    </TableRow>
  );
};

export default HitTableInputRow;
